import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Badge, Button, Loader, SegmentedControl, Select, TextInput } from '@mantine/core'
import DashboardWrapper from '../../components/MainDashboardWrapper/DashboardWrapper'
import { getAdminRole } from '../../helpers/auth'
import { formatDate, timeAgo } from '../../helpers/dateUtils'
import { downloadUserReportCsv, getUserReport, ReportFilters } from './helper'

const PAGE_SIZE = 50

const TIERS = ['Free', 'Network Member', 'Verified', 'Lifetime', 'Lifetime (locked)']
const SEGMENTS = [
  'Incomplete profile',
  'Complete, no live listing',
  'Listed, free',
  'Paying',
  'Lifetime',
  'Participant',
  'Participant, incomplete profile',
]

const calendarLabel: Record<string, string> = { google: 'Google', outlook: 'Outlook', apple: 'Apple' }

const Check = ({ value }: { value: boolean | null }) =>
  value === null ? <span className="text-gray-300">–</span> : value ? <span className="text-green-600">✓</span> : <span className="text-red-500">✗</span>

const day = (iso?: string | null) => (iso ? formatDate(iso) : '–')

export default function ReportsPage() {
  const isSuperAdmin = getAdminRole() === 'super-admin'
  const [filters, setFilters] = useState<ReportFilters>({ type: 'coach', profile: 'any' })
  const [searchInput, setSearchInput] = useState('')
  const [data, setData] = useState<any>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [exporting, setExporting] = useState(false)
  const [visible, setVisible] = useState(PAGE_SIZE)
  const [refreshKey, setRefreshKey] = useState(0)
  // "Refresh now" bypasses the server cache for that one request only.
  const forceRefresh = useRef(false)

  const set = (patch: Partial<ReportFilters>) => setFilters((f) => ({ ...f, ...patch }))

  // Debounce the search box so typing doesn't fire a request per key.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, search: searchInput || undefined })), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    let cancelled = false
    const refresh = forceRefresh.current
    forceRefresh.current = false
    setLoading(true)
    setError('')
    getUserReport(filters, refresh)
      .then((res) => {
        if (cancelled) return
        setData(res.data)
        setVisible(PAGE_SIZE)
      })
      .catch((err) => !cancelled && setError(err.response?.data?.message || 'Could not load the report'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [filters, refreshKey])

  const onExport = () => {
    setExporting(true)
    downloadUserReportCsv(filters)
      .catch(() => setError('Could not export the CSV'))
      .finally(() => setExporting(false))
  }

  const showCoachColumns = filters.type !== 'participant'
  const rows: any[] = data?.rows || []
  const summary = data?.summary

  const cards = useMemo(() => {
    if (!summary) return []
    const out = [{ label: 'Users in view', value: summary.total }]
    if (filters.type === 'all') {
      out.push({ label: 'Coaches', value: summary.coaches }, { label: 'Participants', value: summary.participants })
    }
    out.push({ label: 'Incomplete profiles', value: summary.incompleteProfiles })
    if (showCoachColumns) {
      out.push(
        { label: 'Coaches without a live listing', value: summary.coachesWithoutLiveListing },
        { label: 'Paying coaches', value: summary.payingCoaches },
        { label: 'Stripe connected', value: summary.stripeConnected }
      )
    }
    out.push({ label: 'Calendar connected', value: summary.calendarConnected })
    return out
  }, [summary, filters.type, showCoachColumns])

  return (
    <DashboardWrapper page="reports" title="Reports">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <p className="text-sm font-medium mb-1">Show</p>
            <SegmentedControl
              value={filters.type}
              onChange={(v) => set({ type: v as ReportFilters['type'], tier: undefined, segment: undefined })}
              data={[
                { value: 'coach', label: 'Coaches' },
                { value: 'participant', label: 'Participants' },
                { value: 'all', label: 'Both' },
              ]}
            />
          </div>
          <TextInput
            label="Registered from"
            type="date"
            value={filters.from || ''}
            onChange={(e) => set({ from: e.currentTarget.value || undefined })}
          />
          <TextInput
            label="Registered to"
            type="date"
            value={filters.to || ''}
            onChange={(e) => set({ to: e.currentTarget.value || undefined })}
          />
          <Select
            label="Profile"
            value={filters.profile || 'any'}
            onChange={(v) => set({ profile: (v || 'any') as ReportFilters['profile'] })}
            data={[
              { value: 'any', label: 'Any' },
              { value: 'complete', label: 'Complete' },
              { value: 'incomplete', label: 'Incomplete' },
            ]}
          />
          {showCoachColumns && (
            <Select
              label="Membership tier"
              clearable
              placeholder="Any"
              value={filters.tier || null}
              onChange={(v) => set({ tier: v || undefined })}
              data={TIERS}
            />
          )}
          <Select
            label="Segment"
            clearable
            placeholder="Any"
            value={filters.segment || null}
            onChange={(v) => set({ segment: v || undefined })}
            data={SEGMENTS.filter((s) =>
              filters.type === 'coach' ? !s.startsWith('Participant') : filters.type === 'participant' ? s.startsWith('Participant') : true
            )}
          />
          <TextInput
            label="Search"
            placeholder="Name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
          <p className="text-gray-500 text-sm">
            {data ? `Data as of ${timeAgo(data.generatedAt)} · refreshed every 5 minutes` : ''}
          </p>
          <div className="flex gap-2">
            <Button
              variant="default"
              disabled={loading}
              onClick={() => {
                forceRefresh.current = true
                setRefreshKey((k) => k + 1)
              }}
            >
              Refresh now
            </Button>
            <Button disabled={loading || exporting || !rows.length} loading={exporting} onClick={onExport}>
              Export CSV ({rows.length})
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <Alert color="red" className="mt-4">
          {error}
        </Alert>
      )}
      {data && !data.activityAvailable && (
        <Alert color="orange" className="mt-4">
          Programs and activity dates couldn't be loaded right now, so those columns are blank. Try "Refresh now" in a
          minute.
        </Alert>
      )}

      {cards.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mt-4">
          {cards.map((c) => (
            <div key={c.label} className="bg-white rounded-xl shadow-md p-4">
              <p className="text-2xl font-bold text-[#0B1D3A]">{c.value}</p>
              <p className="text-gray-500 text-sm">{c.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md mt-4 overflow-x-auto">
        {loading && (
          <div className="flex items-center gap-2 text-gray-500 p-6">
            <Loader size="sm" /> Building the report…
          </div>
        )}
        {!loading && rows.length === 0 && !error && <p className="text-gray-500 p-6">No users match these filters.</p>}
        {!loading && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-[#F4F7FC] text-left text-gray-600">
              <tr>
                <th className="p-3">Name / email</th>
                {filters.type === 'all' && <th className="p-3">Type</th>}
                <th className="p-3">Registered</th>
                <th className="p-3">Profile</th>
                {showCoachColumns && <th className="p-3">Stripe</th>}
                <th className="p-3">Calendar</th>
                <th className="p-3">Availability</th>
                {showCoachColumns && <th className="p-3">Programs</th>}
                {showCoachColumns && <th className="p-3">Listings (live / draft)</th>}
                {showCoachColumns && <th className="p-3">Tier</th>}
                {showCoachColumns && <th className="p-3">Payment</th>}
                <th className="p-3">Last login</th>
                <th className="p-3" title="Latest of last login, sessions/chat with clients, programs and listings created">
                  Last activity*
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, visible).map((r) => (
                <tr key={r.id} className="border-t align-top">
                  <td className="p-3">
                    {isSuperAdmin ? (
                      <Link to={`/all-users/${r.id}`} className="font-bold text-[#0B1D3A] hover:underline">
                        {r.name || '(no name)'}
                      </Link>
                    ) : (
                      <p className="font-bold">{r.name || '(no name)'}</p>
                    )}
                    <p className="text-gray-500">
                      {r.email}
                      {!r.emailVerified && (
                        <Badge color="orange" variant="light" size="xs" className="ml-2">
                          unverified
                        </Badge>
                      )}
                    </p>
                    <p className="text-gray-400 text-xs">{r.segment}</p>
                  </td>
                  {filters.type === 'all' && <td className="p-3 capitalize">{r.type}</td>}
                  <td className="p-3 whitespace-nowrap">{day(r.registeredAt)}</td>
                  <td className="p-3">
                    {r.profileComplete ? (
                      <Check value />
                    ) : (
                      <span className="text-red-500">Missing: {r.missing.join(', ')}</span>
                    )}
                  </td>
                  {showCoachColumns && (
                    <td className="p-3">
                      <Check value={r.stripeConnected} />
                    </td>
                  )}
                  <td className="p-3">{r.calendar ? calendarLabel[r.calendar] : <Check value={false} />}</td>
                  <td className="p-3">
                    <Check value={r.availabilitySet} />
                  </td>
                  {showCoachColumns && <td className="p-3">{r.programsCount ?? '–'}</td>}
                  {showCoachColumns && (
                    <td className="p-3">{r.listings ? `${r.listings.published} / ${r.listings.draft}` : '–'}</td>
                  )}
                  {showCoachColumns && <td className="p-3 whitespace-nowrap">{r.tier || '–'}</td>}
                  {showCoachColumns && (
                    <td className="p-3 whitespace-nowrap">
                      {r.paymentStatus || '–'}
                      {r.renewalDate && <p className="text-gray-400 text-xs">renews {day(r.renewalDate)}</p>}
                    </td>
                  )}
                  <td className="p-3 whitespace-nowrap">{day(r.lastSeenAt)}</td>
                  <td className="p-3 whitespace-nowrap">{day(r.lastActivityAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && rows.length > visible && (
          <div className="p-4 border-t text-center">
            <Button variant="subtle" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
              Show more ({rows.length - visible} left)
            </Button>
          </div>
        )}
      </div>
      <p className="text-gray-400 text-xs mt-2">
        * Last login is recorded from the day this report shipped. Last activity is the latest of last login, sessions or
        chat with clients, and programs/listings created.
      </p>
    </DashboardWrapper>
  )
}
